/**
 * JobMockAI — Notification Bell Component
 *
 * Icon button with MUI Badge showing unread count.
 * On click: dropdown panel with latest 5 notifications.
 * Polls GET /api/v1/notifications/unread-count every 30 seconds.
 */
import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Badge,
  Popover,
  Box,
  Typography,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Button,
  Divider,
  CircularProgress,
} from '@mui/material';
import {
  Notifications as NotificationsIcon,
  PersonAdd,
  Handshake,
  EmojiEvents,
  CreditCard,
  Warning,
  Alarm,
  Campaign,
  Circle,
} from '@mui/icons-material';
import api from '../../api/client';

const ICON_MAP = {
  connection_request_received: PersonAdd,
  connection_request_accepted: Handshake,
  badge_earned: EmojiEvents,
  subscription_renewed: CreditCard,
  quota_warning: Warning,
  schedule_reminder: Alarm,
  system_announcement: Campaign,
};

/**
 * Resolve the deep-link destination for a notification using its type + metadata.
 * Kept in sync with the same function in NotificationsPage.js.
 */
const getDeepLink = (notif) => {
  const meta = notif.metadata || notif.data || {};
  switch (notif.type) {
    case 'community_dm':
    case 'new_message':
      return meta.thread_id ? `/community/messages/${meta.thread_id}` : '/community/messages';
    case 'connection_request_received':
    case 'connection_request_accepted':
    case 'connection_request_rejected':
      return '/community/connections';
    case 'badge_earned':
      return '/achievements';
    case 'subscription_renewed':
    case 'subscription_cancelled':
    case 'quota_warning':
      return '/billing';
    case 'schedule_reminder':
    case 'schedule_invite':
    case 'schedule_confirmed':
    case 'schedule_cancelled':
    case 'schedule_no_show':
      return '/schedules';
    case 'schedule_room_ready':
    case 'schedule_room_started':
      return meta.room_id ? `/rooms/${meta.room_id}/lobby` : '/rooms';
    case 'room_invitation':
      return meta.room_code
        ? `/rooms/join/${meta.room_code}`
        : meta.room_id
        ? `/rooms/${meta.room_id}/lobby`
        : '/rooms';
    default:
      return '/notifications';
  }
};

const NotificationBell = () => {
  const navigate = useNavigate();
  const [anchorEl, setAnchorEl] = useState(null);
  const [unreadCount, setUnreadCount] = useState(0);
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(false);

  const fetchUnreadCount = useCallback(async () => {
    try {
      const { data } = await api.notifications.getUnreadCount();
      setUnreadCount(data.count ?? data.unreadCount ?? 0);
    } catch {
      // Silently fail — bell just shows 0
    }
  }, []);

  // Poll every 30 seconds
  useEffect(() => {
    fetchUnreadCount();
    const interval = setInterval(fetchUnreadCount, 30_000);
    return () => clearInterval(interval);
  }, [fetchUnreadCount]);

  const handleOpen = async (event) => {
    setAnchorEl(event.currentTarget);
    setLoading(true);
    try {
      const { data } = await api.notifications.list({ page: 1, limit: 5 });
      setNotifications(data.notifications ?? data.data ?? []);
    } catch {
      setNotifications([]);
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => setAnchorEl(null);

  const handleNotificationClick = async (notif) => {
    handleClose();
    // Mark as read (fire-and-forget)
    try { await api.notifications.markRead(notif.id); } catch { /* silent */ }
    // Optimistically decrement badge
    setUnreadCount(prev => Math.max(0, prev - (notif.is_read || notif.isRead ? 0 : 1)));
    navigate(getDeepLink(notif));
  };

  const handleViewAll = () => {
    handleClose();
    navigate('/notifications');
  };

  const open = Boolean(anchorEl);

  return (
    <>
      <Box
        component="button"
        type="button"
        onClick={handleOpen}
        aria-label={`${unreadCount} unread notifications`}
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 0.15,
          px: { xs: 0.75, md: 1.25 },
          py: 0.5,
          minWidth: { md: 72 },
          border: 'none',
          bgcolor: 'transparent',
          color: 'text.secondary',
          cursor: 'pointer',
          borderRadius: 1,
          '&:hover': {
            color: 'text.primary',
            bgcolor: 'action.hover',
          },
        }}
      >
        <Badge badgeContent={unreadCount} color="error" max={99}>
          <NotificationsIcon sx={{ fontSize: { xs: 22, md: 24 } }} />
        </Badge>
        <Typography
          component="span"
          variant="caption"
          sx={{
            display: { xs: 'none', md: 'block' },
            fontSize: '0.7rem',
            fontWeight: 500,
            lineHeight: 1.2,
            whiteSpace: 'nowrap',
          }}
        >
          Notifications
        </Typography>
      </Box>

      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        PaperProps={{ sx: { width: 360, maxHeight: 420 } }}
      >
        <Box sx={{ p: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="subtitle1" fontWeight={700}>
            Notifications
          </Typography>
          {unreadCount > 0 && (
            <Typography variant="caption" color="primary">
              {unreadCount} new
            </Typography>
          )}
        </Box>
        <Divider />

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
            <CircularProgress size={24} />
          </Box>
        ) : notifications.length === 0 ? (
          <Box sx={{ py: 4, textAlign: 'center' }}>
            <Typography variant="body2" color="text.secondary">
              No notifications yet
            </Typography>
          </Box>
        ) : (
          <List disablePadding>
            {notifications.map((n) => {
              const Icon = ICON_MAP[n.type] || NotificationsIcon;
              const isUnread = !n.is_read && !n.isRead;
              return (
                <ListItem
                  key={n.id}
                  button
                  onClick={() => handleNotificationClick(n)}
                  sx={{
                    borderLeft: isUnread ? '3px solid' : '3px solid transparent',
                    borderColor: isUnread ? 'primary.main' : 'transparent',
                  }}
                >
                  <ListItemIcon sx={{ minWidth: 36 }}>
                    <Icon fontSize="small" color={isUnread ? 'primary' : 'action'} />
                  </ListItemIcon>
                  <ListItemText
                    primary={n.title}
                    secondary={n.body || n.message}
                    primaryTypographyProps={{
                      fontSize: '0.85rem',
                      fontWeight: isUnread ? 700 : 400,
                      noWrap: true,
                    }}
                    secondaryTypographyProps={{ fontSize: '0.75rem', noWrap: true }}
                  />
                  {isUnread && <Circle sx={{ fontSize: 8, color: 'primary.main', ml: 1 }} />}
                </ListItem>
              );
            })}
          </List>
        )}

        <Divider />
        <Box sx={{ p: 1, textAlign: 'center' }}>
          <Button size="small" onClick={handleViewAll}>
            View All Notifications
          </Button>
        </Box>
      </Popover>
    </>
  );
};

export default NotificationBell;
